import type {BreadcrumbItem} from 'src/app/layout/BackBar';

/**
 * Configuration for page navigation (breadcrumbs and back button).
 *
 * This provides a declarative way to configure navigation for detail pages
 * (view, edit, create) without inline computation in components.
 *
 * Usage:
 * ```tsx
 * const navConfig: PageNavigationConfig = {
 *   entityName: 'Risk Assessment Code',
 *   entityNamePlural: 'Risk Assessment Codes',
 *   listPath: PathUtility.RiskAssessment(),
 *   viewPath: (id) => PathUtility.RiskAssessmentView(id, 'content'),
 * };
 *
 * const {breadcrumbs, fallbackTo, fallbackLabel} = buildPageNavigation(navConfig, {
 *   mode: 'edit',
 *   id: 123,
 *   itemName: 'Safety Assessment Matrix',
 * });
 * ```
 */

export interface PageNavigationConfig {
  /** Singular entity name (e.g., "Instance", "Risk Assessment Code") */
  entityName: string;

  /** Plural entity name for list page (e.g., "Instances", "Risk Assessment Codes") */
  entityNamePlural: string;

  /** URL path to the list page */
  listPath: string;

  /** Function to build view page URL from entity ID */
  viewPath: (id: number | string) => string;

  /** Optional parent breadcrumb (e.g., the linked System directory for global admin pages) */
  parentBreadcrumb?: BreadcrumbItem;

  /** Optional custom labels for the back button */
  backLabels?: {
    /** Label when on list/create page (default: "Back to {entityNamePlural}") */
    toList?: string;
    /** Label when on edit page (default: "Cancel") */
    cancel?: string;
  };
}

export interface PageNavigationContext {
  /** Current page mode */
  mode: 'view' | 'edit' | 'create';

  /** Entity ID (required for view/edit modes) */
  id?: number | string;

  /** Display name of the current item (shown in breadcrumbs) */
  itemName?: string;
}

export interface PageNavigationResult {
  /** Breadcrumb trail for BackBar */
  breadcrumbs: BreadcrumbItem[];

  /** URL to navigate back to */
  fallbackTo: string;

  /** Label for back button */
  fallbackLabel: string;
}

/**
 * Builds navigation configuration (breadcrumbs and back button) based on
 * declarative config and current context.
 */
export function buildPageNavigation(
  config: PageNavigationConfig,
  context: PageNavigationContext
): PageNavigationResult {
  const {
    entityName,
    entityNamePlural,
    listPath,
    viewPath,
    parentBreadcrumb,
    backLabels,
  } = config;
  const {mode, id, itemName} = context;

  const displayName = itemName ?? entityName;
  const toListLabel = backLabels?.toList ?? `Back to ${entityNamePlural}`;
  const cancelLabel = backLabels?.cancel ?? 'Cancel';

  // Build base breadcrumbs
  const baseBreadcrumbs: BreadcrumbItem[] = [];
  if (parentBreadcrumb) {
    baseBreadcrumbs.push(parentBreadcrumb);
  }
  baseBreadcrumbs.push({label: entityNamePlural, to: listPath});

  // Build breadcrumbs based on mode
  let breadcrumbs: BreadcrumbItem[];
  switch (mode) {
    case 'create':
      breadcrumbs = [...baseBreadcrumbs, {label: `New ${entityName}`}];
      break;

    case 'edit':
      if (!id) {
        throw new Error('ID is required for edit mode');
      }
      breadcrumbs = [
        ...baseBreadcrumbs,
        {label: displayName, to: viewPath(id)},
        {label: 'Edit'},
      ];
      break;

    case 'view':
    default:
      breadcrumbs = [...baseBreadcrumbs, {label: displayName}];
      break;
  }

  // Determine back button config based on mode
  const isEditMode = mode === 'edit';
  const fallbackTo = isEditMode && id ? viewPath(id) : listPath;
  const fallbackLabel = isEditMode ? cancelLabel : toListLabel;

  return {breadcrumbs, fallbackTo, fallbackLabel};
}

/**
 * Pre-built navigation configs for common entity types.
 * Import and use directly, or use as reference for creating custom configs.
 */
export const NavigationConfigs = {
  // Add pre-built configs here as needed
  // Example:
  // RiskAssessmentCode: { ... } as PageNavigationConfig,
};
